/**
 * Google Analytics 4 Service
 * 
 * Сервис для работы с Google Analytics в веб-приложении
 */

// Типы событий
export interface AnalyticsEvent {
  name: string;
  params?: Record<string, any>;
}

// Конфигурация GA4
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

/**
 * Инициализация Google Analytics
 */
export const initializeAnalytics = (): void => {
  if (!GA4_MEASUREMENT_ID) {
    console.warn('⚠️ Google Analytics: VITE_GA4_MEASUREMENT_ID не установлен');
    return;
  }

  // Проверяем, загружен ли уже скрипт
  if (typeof window.gtag === 'function') {
    console.log('✅ Google Analytics уже инициализирован');
    return;
  }

  // Загружаем скрипт GA4
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Инициализируем gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: false, // Отключаем автоматическую отправку page_view
  });

  console.log('✅ Google Analytics инициализирован:', GA4_MEASUREMENT_ID);
};

/**
 * Отправка события в Google Analytics
 */
export const trackEvent = (eventName: string, params?: Record<string, any>): void => {
  if (!GA4_MEASUREMENT_ID || typeof window.gtag !== 'function') {
    console.warn('⚠️ Google Analytics не инициализирован');
    return;
  }

  window.gtag('event', eventName, params);
  console.log('📊 GA Event:', eventName, params);
};

/**
 * Отправка просмотра страницы
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

/**
 * Отправка пользовательских свойств
 */
export const setUserProperties = (properties: Record<string, any>): void => {
  if (!GA4_MEASUREMENT_ID || typeof window.gtag !== 'function') {
    console.warn('⚠️ Google Analytics не инициализирован');
    return;
  }

  window.gtag('set', 'user_properties', properties);
  console.log('👤 GA User Properties:', properties);
};

/**
 * Установка User ID
 */
export const setUserId = (userId: string | number): void => {
  if (!GA4_MEASUREMENT_ID || typeof window.gtag !== 'function') {
    console.warn('⚠️ Google Analytics не инициализирован');
    return;
  }

  window.gtag('config', GA4_MEASUREMENT_ID, {
    user_id: String(userId),
  });
  console.log('👤 GA User ID set:', userId);
};

// ============================================
// 📊 Предопределенные события для Alveon
// ============================================

/**
 * Авторизация пользователя
 */
export const trackLogin = (method: 'email' | 'google' | 'apple' | 'telegram'): void => {
  trackEvent('login', { method });
};

/**
 * Регистрация пользователя
 */
export const trackSignUp = (method: 'email' | 'google' | 'apple' | 'telegram'): void => {
  trackEvent('sign_up', { method });
};

/**
 * Создание транзакции
 */
export const trackAddTransaction = (type: 'income' | 'expense', category: string, amount: number, currency: string): void => {
  trackEvent('add_transaction', {
    transaction_type: type,
    category,
    amount,
    currency,
  });
};

/**
 * Удаление транзакции
 */
export const trackDeleteTransaction = (type: 'income' | 'expense'): void => {
  trackEvent('delete_transaction', {
    transaction_type: type,
  });
};

/**
 * Редактирование транзакции
 */
export const trackEditTransaction = (type: 'income' | 'expense'): void => {
  trackEvent('edit_transaction', {
    transaction_type: type,
  });
};

/**
 * Создание финансовой цели
 */
export const trackCreateGoal = (goalName: string, targetAmount: number): void => {
  trackEvent('create_goal', {
    goal_name: goalName,
    target_amount: targetAmount,
  });
};

/**
 * Создание лимита расходов
 */
export const trackCreateLimit = (category: string, amount: number): void => {
  trackEvent('create_limit', {
    category,
    amount,
  });
};

/**
 * Экспорт данных
 */
export const trackExportData = (format: 'excel' | 'csv' | 'pdf'): void => {
  trackEvent('export_data', { format });
};

/**
 * Просмотр аналитики
 */
export const trackViewAnalytics = (period: 'day' | 'week' | 'month' | 'year'): void => {
  trackEvent('view_analytics', { period });
};

/**
 * AI чат - отправка сообщения
 */
export const trackAIChatMessage = (): void => {
  trackEvent('ai_chat_message');
};

/**
 * Подключение банковского аккаунта
 */
export const trackConnectBank = (provider: string): void => {
  trackEvent('connect_bank', { provider });
};

/**
 * Ошибка
 */
export const trackError = (errorType: string, errorMessage: string): void => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
  });
};

/**
 * Покупка подписки
 */
export const trackPurchase = (plan: string, amount: number, currency: string): void => {
  trackEvent('purchase', {
    plan,
    amount,
    currency,
  });
};

// Типизация для window.gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

